import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { type Task, type Client, type Invoice } from "@shared/schema";
// import { calculateSimpleTaskAmount, convertTaskForFrontend, convertTaskItemsForFrontend } from "@shared/task-calculations";
import { EnhancedTaskForm } from "@/components/enhanced-task-form";
import { QuickTaskModal } from "@/components/quick-task-modal";
import { TaskTypeBadge } from "@/components/task-type-badge";
import { TaskDrawer } from "@/components/task-drawer";
import { PersonalTaskView } from "@/components/personal-task-view";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { text } from "@/lib/i18n";
import { getLocalizedUrl } from "@/lib/localized-routes";
import { formatMoney, formatTax } from "@/lib/format-helpers";

// Safe formatting helpers
const toNumber = (v: unknown, fallback = 0): number => {
  const n = typeof v === 'string' ? Number(v) : (typeof v === 'number' ? v : NaN);
  return Number.isFinite(n) ? n : fallback;
};

const money = (v: unknown, currency = 'EUR'): string =>
  new Intl.NumberFormat('sk-SK', { style: 'currency', currency })
    .format(toNumber(v));

// Safe total computation with null-safety - ONLY for hours calculation
const computeTotal = (task: any, client?: any): number => {
  // Always calculate hours * rate, don't use pre-computed totalAmount
  // because we want to add items separately in computeTaskTotal
  
  const hours = toNumber(task.actualHours || task.hours_worked || task.hoursWorked, 0);
  const rate = toNumber(task.hourlyRate || task.hourly_rate || (client?.hourlyRate) || (client?.hourly_rate), 0);
  
  
  return hours * rate;
};
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ErrorBoundary } from "react-error-boundary";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus, Star, Search, Pencil, ChevronDown, ChevronRight, AlertCircle, Link, Share2, Mail, CreditCard, Users, FileText, Filter, MoreHorizontal, ClipboardList, Copy } from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// import { statusLabel } from "@/shared/statusLabel"; // Removed - function doesn't exist

// Calculate total: use API calculated totals instead of client-side computation
function computeTaskTotal(task: Task, client?: Client, user?: any) {
  // If task has API calculated totals, use them
  if (task.subtotal_cents !== undefined && task.tax_cents !== undefined && task.total_cents !== undefined) {
    // If user is not VAT payer, use subtotal (without VAT), otherwise use total (with VAT)
    if (user?.isVatPayer === true) {
      return task.total_cents / 100; // Convert cents to euros (with VAT)
    } else {
      return task.subtotal_cents / 100; // Convert cents to euros (without VAT)
    }
  }
  
  // Fallback to old calculation for backward compatibility
  const baseTotal = computeTotal(task, client);
  
  // Add items amount (calculate net amount from unit_price and quantity)
  let itemsNetAmount = 0;
  if (task.items && Array.isArray(task.items)) {
    itemsNetAmount = task.items.reduce((sum, item) => {
      const quantity = toNumber(item.quantity || 0);
      const unitPrice = toNumber(item.unit_price || 0);
      const amount = toNumber(item.amount || 0);
      // Use pre-calculated amount if available, otherwise calculate it
      return sum + (amount > 0 ? amount : quantity * unitPrice);
    }, 0);
  }
  
  // Calculate total without VAT
  const totalWithoutVat = baseTotal + itemsNetAmount;
  
  // If user is VAT payer, add VAT to the total
  if (user?.isVatPayer === true && totalWithoutVat > 0) {
    // Use user's VAT rate instead of client's VAT rate
    const vatRate = toNumber(user?.companyVatRate || 20);
    const vatAmount = totalWithoutVat * vatRate / 100;
    const result = totalWithoutVat + vatAmount;
    
    return result;
  }
  
  return totalWithoutVat;
}

// Helper function to group tasks by month
function groupTasksByMonth(tasks: Task[], clients?: Client[], user?: any) {
  const grouped = tasks.reduce((acc, task) => {
    // Bezpečné vytvorenie dátumu
    let date: Date;
    if (task.dueDate) {
      const dueDate = new Date(task.dueDate);
      date = isNaN(dueDate.getTime()) ? new Date(task.createdAt!) : dueDate;
    } else {
      date = new Date(task.createdAt!);
    }
    
    // Ak je aj createdAt neplatný, použijeme aktuálny dátum
    if (isNaN(date.getTime())) {
      date = new Date();
    }
    
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!acc[key]) {
      acc[key] = {
        tasks: [],
        totalAmount: 0
      };
    }
    // Pridať úlohu do poľa úloh pre daný mesiac
    acc[key].tasks.push(task);
    
    // Use API calculated totals if available
    if (task.subtotal_cents !== undefined && task.total_cents !== undefined) {
      // If user is not VAT payer, use subtotal (without VAT), otherwise use total (with VAT)
      acc[key].totalAmount += (user?.isVatPayer === true) ? (task.total_cents / 100) : (task.subtotal_cents / 100);
    } else if (Array.isArray(clients)) {
      // Fallback to old calculation for backward compatibility
      const client = Array.isArray(clients) ? clients.find(c => c.id === task.clientId) : undefined;
      acc[key].totalAmount += computeTaskTotal(task, client, user);
    }
    
    return acc;
  }, {} as Record<string, { tasks: Task[], totalAmount: number }>);

  return Object.entries(grouped)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, data]) => [
      key,
      Array.isArray(data.tasks) ? data.tasks.sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate) : new Date(a.createdAt!);
        const dateB = b.dueDate ? new Date(b.dueDate) : new Date(b.createdAt!);
        return dateB.getTime() - dateA.getTime();
      }) : [],
      data.totalAmount || 0
    ]);
}

function formatMonth(dateKey: string) {
  // Ak je dateKey neplatný, vrátime fallback
  if (!dateKey || dateKey === 'Invalid Date') {
    return 'Bez dátumu';
  }
  
  const [year, month] = dateKey.split('-');
  const yearNum = parseInt(year);
  const monthNum = parseInt(month);
  
  // Ak sú čísla neplatné, vrátime fallback
  if (isNaN(yearNum) || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    return 'Bez dátumu';
  }
  
  const date = new Date(yearNum, monthNum - 1);
  return date.toLocaleString('sk-SK', { month: 'long', year: 'numeric' });
}

export default function Tasks() {
  const TASKS_PER_PAGE = 50;
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined);
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState<string | undefined>(undefined);
  const [starredFilter, setStarredFilter] = useState<boolean | undefined>(undefined);
  const [personalViewOpen, setPersonalViewOpen] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(() => {
    // Rozbaľ aktuálny mesiac by default
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return new Set([currentMonth]);
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);
  const [planLimitError, setPlanLimitError] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [isCreateInvoiceDialogOpen, setIsCreateInvoiceDialogOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [hideInvoicedTasks, setHideInvoicedTasks] = useState(true);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const { t } = useTranslation(['tasks', 'common', 'nav', 'filters', 'notifications', 'modals']);

  // Ak nie je user, zobraz fallback
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="p-4 text-sm text-muted-foreground">Redirecting...</div>
      </div>
    );
  }

  const handleCreateDialogOpenChange = (open: boolean) => {
    // Vytváranie úlohy: zavretie riešime iba cez X.
    if (open) setIsCreateDialogOpen(true);
  };

  const handleEditDialogOpenChange = (open: boolean) => {
    // Editácia úlohy: zavretie riešime iba cez X.
    if (open) setEditingTaskId((v) => v);
  };

  useEffect(() => {
    const onClickCapture = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const clickedClose = !!target?.closest?.('[data-dialog-close="true"]');
      if (!clickedClose) return;

      // Zatvoríme iba aktuálne otvorený dialog (buď create, alebo edit).
      if (editingTaskId) {
        setEditingTaskId(null);
      } else if (isCreateDialogOpen) {
        setIsCreateDialogOpen(false);
      } else if (isQuickCreateOpen) {
        setIsQuickCreateOpen(false);
      }
    };

    document.addEventListener("click", onClickCapture, true);
    return () => document.removeEventListener("click", onClickCapture, true);
  }, [editingTaskId, isCreateDialogOpen, isQuickCreateOpen]);

  const {
    data: tasksPages,
    isLoading: tasksLoading,
    error: tasksError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["/api/tasks", "infinite"],
    enabled: !!user,
    staleTime: 30000, // 30 seconds
    cacheTime: 300000, // 5 minutes
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const page = typeof pageParam === "number" ? pageParam : 1;
      console.log('[TASKS] Fetching tasks, user:', user?.email, 'role:', user?.role, 'page:', page);
      const response = await apiRequest('GET', `/api/tasks?page=${page}&perPage=${TASKS_PER_PAGE}`);
      console.log('[TASKS] Response status:', response.status);
      const data = await response.json();
      console.log('[TASKS] Response data:', data);
      return data;
    },
    getNextPageParam: (lastPage: any) => {
      const page = toNumber(lastPage?.page, 1);
      const more = !!lastPage?.hasMore;
      return more ? page + 1 : undefined;
    },
  });

  const tasks: Task[] = useMemo(() => {
    const pages = tasksPages?.pages ?? [];
    const allItems = pages.flatMap((p: any) => (Array.isArray(p?.items) ? p.items : []));
    const mapped = allItems.map((task: any) => ({
      id: task.id,
      title: task.title ?? '',
      description: task.description ?? '',
      clientId: task.client_id ?? null,
      clientName: task.client_name ?? '',
      status: task.status ?? 'pending',
      priority: task.priority ?? 'medium',
      taskType: task.task_type ?? 'standard',
      dueDate: task.due_date,
      createdAt: task.created_at,
      updatedAt: task.updated_at,
      completedAt: task.completed_at,
      isStarred: task.is_starred ?? false,
      isInvoiced: task.is_invoiced ?? false,
      invoiceId: task.invoice_id,
      // Finančné údaje
      totalAmount: toNumber(task.total_amount ?? task.manual_amount ?? 0),
      hourlyRate: toNumber(task.hourly_rate ?? 0),
      actualHours: toNumber(task.actual_hours ?? task.hours_worked ?? 0),
      estimatedHours: toNumber(task.estimated_hours ?? 0),
      manualAmount: toNumber(task.manual_amount ?? 0),
      // Časové údaje
      spentMinutes: toNumber(task.spent_minutes ?? 0),
      estimatedMinutes: toNumber(task.estimated_hours ?? 0) * 60,
      // Položky
      items: task.items || [],
      totals: task.totals,
      billable: task.billable ?? false,
      assignedToId: task.assigned_to_id ?? task.assignedToId ?? null,
      // Finančné súčty z API
      subtotal_cents: task.subtotal_cents,
      tax_cents: task.tax_cents,
      total_cents: task.total_cents,
      displayNumber: task.display_number ?? task.displayNumber ?? null,
      accept_deadline: task.accept_deadline,
      acceptDeadline: task.accept_deadline,
    })) as Task[];

    // Dedup (safety) by id in case of cache weirdness
    const byId = new Map<number, Task>();
    for (const t of mapped) byId.set((t as any).id, t);
    return Array.from(byId.values());
  }, [tasksPages]);

  // Debug log pre tasks query
  console.log('[TASKS] Query state:', { 
    user: user?.email, 
    userExists: !!user, 
    enabled: !!user, 
    isLoading: tasksLoading, 
    error: tasksError?.message,
    tasksCount: tasks?.length,
    hasNextPage,
    isFetchingNextPage,
  });

  const {
    data: clients,
    isLoading: clientsLoading,
    error: clientsError,
  } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
    enabled: hasPermission('clients', 'view'),
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/clients");
      const data = await res.json();
      // API vracia objekty s items, nie priamo pole
      const items = Array.isArray(data) ? data : data.items || [];
      // Map snake_case to camelCase
      return items.map((client: any) => ({
        ...client,
        hourlyRate: client.hourly_rate || client.hourlyRate || "0",
      }));
    },
  });

  // Interface pre team members v tasks komponente
  interface TeamMember {
    id: number;
    invitedEmail: string;
    memberName?: string;
    firstName?: string;
    lastName?: string;
    role: string;
    status: string;
  }

  const {
    data: teamMembers,
    isLoading: teamMembersLoading,
    error: teamMembersError,
  } = useQuery<TeamMember[]>({
    queryKey: ["/api/team-members"],
    enabled: hasPermission('tasks', 'view'),
    queryFn: async () => {
      try {
        const res = await apiRequest('GET', '/api/team-members');
        const data = await res.json();
        return Array.isArray(data) ? data : (data?.items || []);
      } catch (e) {
        // 403 = nemá prístup k tímu - vráť prázdne pole
        if ((e as Error)?.message?.includes('403')) return [];
        throw e;
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/tasks", data);
      return res.json();
    },
    onSuccess: async () => {
      // Vynútiť kompletné obnovenie cache - rovnaké ako v updateMutation
      queryClient.removeQueries({ queryKey: ["/api/tasks"] });
      queryClient.removeQueries({ queryKey: ["/api/tasks/statistics"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks/statistics"] });
      toast({
        title: t('notifications:created'),
        description: t('notifications:created') + ". " + t('modals:taskItem:modalStaysOpen'),
      });
    },
    onError: (error: Error) => {
      // Skontrolujeme, či ide o chybu limitu plánu
      if (error.message.includes("Free plan limit reached") || error.message.includes("upgrade")) {
        // Nastavíme stav pre zobrazenie priateľskej notifikácie
        setPlanLimitError(true);
        
        // Zatvoríme dialóg na vytváranie úlohy
        setIsCreateDialogOpen(false);
      } else {
        toast({
          title: t('notifications:info'),
          description: error.message,
          variant: "default",
        });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      // Submitting task data
      const res = await apiRequest("PATCH", `/api/tasks/${id}`, data);
      return res.json();
    },
    onSuccess: async () => {
      // Vynútiť kompletné obnovenie cache
      queryClient.removeQueries({ queryKey: ["/api/tasks"] });
      queryClient.removeQueries({ queryKey: ["/api/tasks/statistics"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks/statistics"] });
      setEditingTaskId(null);
      toast({
        title: t('notifications:updated'),
        description: t('notifications:updated'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('notifications:info'),
        description: error.message,
        variant: "default",
      });
    },
  });

  const toggleStarMutation = useMutation({
    mutationFn: async ({ id, isStarred }: { id: number; isStarred: boolean }) => {
      const res = await apiRequest("PATCH", `/api/tasks/${id}`, { is_starred: isStarred });
      return res.json();
    },
    onSuccess: async () => {
      // Vynútiť kompletné obnovenie cache
      queryClient.removeQueries({ queryKey: ["/api/tasks"] });
      queryClient.removeQueries({ queryKey: ["/api/tasks/starred"] });
      queryClient.removeQueries({ queryKey: ["/api/tasks/statistics"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks/starred"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks/statistics"] });
      toast({
        title: t('notifications:updated'),
        description: t('notifications:taskStatusChanged'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('notifications:info'),
        description: error.message,
        variant: "default",
      });
    },
  });
  
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/tasks/${id}`);
    },
    onSuccess: async () => {
      // Vynútiť kompletné obnovenie cache
      queryClient.removeQueries({ queryKey: ["/api/tasks"] });
      queryClient.removeQueries({ queryKey: ["/api/tasks/statistics"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks/statistics"] });
      toast({
        title: t('notifications:deleted'),
        description: t('notifications:deleted'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('notifications:info'),
        description: error.message,
        variant: "default",
      });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/tasks/${id}/duplicate`);
      return res.json();
    },
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: ["/api/tasks"] });
      queryClient.removeQueries({ queryKey: ["/api/tasks/statistics"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks/statistics"] });
      toast({
        title: t('notifications:updated'),
        description: t('tasks:duplicateSuccess') || "Úloha bola duplikovaná",
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common:error'),
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const toggleStarred = useMutation({
    mutationFn: async ({ id, isStarred }: { id: number; isStarred: boolean }) => {
      const res = await apiRequest("PATCH", `/api/tasks/${id}`, { is_starred: isStarred });
      return res.json();
    },
    onSuccess: async () => {
      // Vynútiť kompletné obnovenie cache
      queryClient.removeQueries({ queryKey: ["/api/tasks"] });
      queryClient.removeQueries({ queryKey: ["/api/tasks/statistics"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks/statistics"] });
    },
    onError: (error: Error) => {
      toast({
        title: t('notifications:info'),
        description: error.message,
        variant: "default",
      });
    },
  });

  // Only consider loading/error states from queries that are actually enabled
  const isLoading = tasksLoading || (hasPermission('clients', 'view') && clientsLoading);
  // Only consider errors from active queries - ignore clients error if user doesn't have permission
  // Also ignore 403/Forbidden errors for clients query (user might not have permission)
  const isForbiddenClients = clientsError && 
    (clientsError as any)?.message?.includes('Forbidden') || 
    (clientsError as any)?.message?.includes('403');
  const error = tasksError || (hasPermission('clients', 'view') && clientsError && !isForbiddenClients);

  const filteredTasks = tasks?.filter((task) => {
    const matchesSearch = (task.title ?? '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || statusFilter === "all" || task.status === statusFilter;
    const matchesClient = !selectedClientId || selectedClientId === "all" || task.clientId?.toString() === selectedClientId;
    const matchesTeamMember = !selectedTeamMemberId || selectedTeamMemberId === "all" || 
      (selectedTeamMemberId === "unassigned" ? !task.assignedToId : task.assignedToId?.toString() === selectedTeamMemberId);
    const matchesStarred = starredFilter === undefined || (starredFilter === true ? task.isStarred : !task.isStarred);
    const matchesInvoiced = hideInvoicedTasks ? !task.isInvoiced : true;
    return matchesSearch && matchesStatus && matchesClient && matchesTeamMember && matchesStarred && matchesInvoiced;
  });

  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  
  // Create invoice mutation
  const createInvoiceMutation = useMutation({
    mutationFn: async (data: {
      invoiceData: Partial<Invoice>;
      taskIds: number[];
    }) => {
      const res = await apiRequest("POST", "/api/invoices", data);
      return res.json();
    },
    onSuccess: async () => {
      // Vynútiť kompletné obnovenie cache
      queryClient.removeQueries({ queryKey: ["/api/invoices"] });
      queryClient.removeQueries({ queryKey: ["/api/tasks"] });
      queryClient.removeQueries({ queryKey: ["/api/tasks/statistics"] });
      await queryClient.refetchQueries({ queryKey: ["/api/invoices"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks/statistics"] });
      setIsCreateInvoiceDialogOpen(false);
      setSelectedTaskIds([]);
      toast({
        title: t('notifications:invoiceCreated'),
        description: t('notifications:invoiceCreatedDescription'),
      });
      window.location.href = "/invoices";
    },
    onError: (error: Error) => {
      toast({
        title: t('notifications:info'),
        description: `Nepodarilo sa vytvoriť faktúru: ${error.message}`,
        variant: "default",
      });
    },
  });
  
  // Calculate totals for selected tasks
  const calculateInvoiceTotals = (taskIds: number[]) => {
    // Filter tasks based on provided IDs
    const tasksToInvoice = tasks?.filter(task => taskIds.includes(task.id)) || [];
    
    // Calculate total amount using API calculated totals
    // For invoices, we always use subtotal (without VAT) and then add VAT if user is VAT payer
    const totalAmount = tasksToInvoice.reduce((sum, task) => {
      // Use API calculated totals if available
      if (task.subtotal_cents !== undefined) {
        return sum + (task.subtotal_cents / 100); // Always use subtotal (without VAT) for invoice calculation
      }
      if (task.total_cents !== undefined) {
        // Fallback: if only total_cents is available, we need to subtract VAT if it was included
        // But this shouldn't happen if recalcTaskTotalsWithItems is called properly
        return sum + (task.total_cents / 100);
      }
      
      // Fallback to server totalAmount if available
      const taskWithTotal = task as any; // Temporary type assertion
      if (taskWithTotal.totalAmount !== undefined) {
        return sum + taskWithTotal.totalAmount;
      }
      
      // Fallback na starý výpočet ak server nevrátil totalAmount
      let taskAmount = 0;
      if (task.type === "cash" || task.type === "offset") {
        taskAmount = task.manualAmount ? parseFloat(toNumber(task.manualAmount).toString()) : 0;
        // Pre zápočty už máme záporné sumy v databáze, takže ich používame tak ako sú
      } else {
        const client = Array.isArray(clients) ? clients.find(c => c.id === task.clientId) : undefined;
        const rate = client ? Number(client.hourlyRate) : 0;
        const hours = parseFloat(task.hoursWorked || '0');
        taskAmount = rate * (isNaN(hours) ? 0 : hours);
      }
      
      // Zabezpečíme, že suma nie je NaN
      if (isNaN(taskAmount)) {
        taskAmount = 0;
      }
      
      return sum + taskAmount;
    }, 0);
    
    // Calculate tax only if the user is a VAT payer
    const isVatPayer = user?.isVatPayer === true;
    const vatRate = user?.companyVatRate || 20;
    const totalTax = isVatPayer ? totalAmount * (vatRate / 100) : 0;
    const totalWithTax = totalAmount + totalTax;
    
    // Zabezpečíme, že všetky sumy nie sú NaN
    const safeTotalAmount = isNaN(totalAmount) ? 0 : totalAmount;
    const safeTotalTax = isNaN(totalTax) ? 0 : totalTax;
    const safeTotalWithTax = isNaN(totalWithTax) ? 0 : totalWithTax;
    
    return { 
      totalAmount: safeTotalAmount, 
      totalTax: safeTotalTax, 
      totalWithTax: safeTotalWithTax, 
      tasksToInvoice 
    };
  };

  // Handle task selection toggle
  const toggleTaskSelection = (taskId: number) => {
    setSelectedTaskIds(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      } else {
        return [...prev, taskId];
      }
    });
  };
  
  // Handle create invoice from selected tasks
  const handleCreateInvoiceClick = () => {

    if (selectedTaskIds.length === 0) {
      toast({
        title: t('notifications:warning'),
        description: t('notifications:selectTasksToInvoice'),
        variant: "default",
      });
      return;
    }
    
    // Find client for the selected tasks
    const selectedTask = tasks?.find(task => task.id === selectedTaskIds[0]);
    if (!selectedTask) return;
    
    const clientId = selectedTask.clientId;
    
    // Check if all selected tasks are for the same client
    const allSameClient = selectedTaskIds.every(taskId => {
      const task = tasks?.find(t => t.id === taskId);
      return task && task.clientId === clientId;
    });
    
    if (!allSameClient) {
      toast({
        title: t('notifications:info'),
        description: "Vyfakturovať je možné len úlohy pre rovnakého klienta",
        variant: "default",
      });
      return;
    }
    
    // Calculate totals
    const { totalAmount, totalTax, totalWithTax } = calculateInvoiceTotals(selectedTaskIds);
    
    const selectedClient = Array.isArray(clients) ? clients.find(c => c.id === clientId) : undefined;
    if (!selectedClient) return;

    // Generate invoice number in format 2025-INV-0001
    const today = new Date();
    const year = today.getFullYear();
    
    // Auto-generated invoice sequence number based on existing invoices
    const sequenceNumber = 1;
    
    const invoiceNumber = `${year}-INV-${sequenceNumber.toString().padStart(4, '0')}`;
    
    // Calculate due date (default 14 days)
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + (selectedClient?.dueDays || 14));

    // Build display_items from selected tasks
    const displayItems: any[] = [];
    for (const taskId of selectedTaskIds) {
      const task = tasks?.find(t => t.id === taskId);
      if (!task) continue;
      
      // Add hours as item
      if (task.hoursWorked && task.hoursWorked > 0) {
        const hourlyRate = parseFloat(selectedClient?.hourlyRate || "0");
        displayItems.push({
          name: task.title,
          description: task.description || "",
          quantity: parseFloat(task.hoursWorked.toString()),
          unit: "hod",
          price: hourlyRate,
          vat_rate: parseFloat(user?.companyVatRate || "0") / 100
        });
      }
      
      // Add task items
      if (task.items && Array.isArray(task.items) && task.items.length > 0) {
        for (const item of task.items) {
          displayItems.push({
            name: item.name,
            description: item.description || "",
            quantity: parseFloat(item.quantity?.toString() || "1"),
            unit: "ks",
            price: parseFloat(item.unitPrice?.toString() || "0"),
            vat_rate: parseFloat(user?.companyVatRate || "0") / 100
          });
        }
      }
    }

    const invoiceData = {
      clientId: clientId,
      invoiceNumber: invoiceNumber,
      status: 'draft' as 'draft' | 'sent' | 'paid' | 'cancelled',
      issueDate: today.toISOString(),
      dueDate: dueDate.toISOString(),
      totalAmount: totalAmount,
      taxAmount: totalTax,
      totalWithTax: totalWithTax,
      currency: 'EUR',
      notes: 'Faktúra vytvorená z úloh',
      displayItems: displayItems
    };

    console.log('[FRONTEND] Creating invoice with data:', invoiceData);
    console.log('[FRONTEND] Invoice totals:', { totalAmount, totalTax, totalWithTax });

    createInvoiceMutation.mutate({ invoiceData, taskIds: selectedTaskIds });
  };

  const toggleMonth = (month: string) => {
    // Vždy ponechaj aktuálny mesiac rozbalený
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    const newExpanded = new Set(expandedMonths);
    
    // Ak sa pokúšame zbalit aktuálny mesiac, necháme ho rozbalený
    if (month === currentMonth) {
      // Aktuálny mesiac musí zostať rozbalený
      newExpanded.add(currentMonth);
    } else {
      // Pre iné mesiace funguje normálne zbalovanie/rozbalovanie
      if (newExpanded.has(month)) {
        newExpanded.delete(month);
      } else {
        newExpanded.add(month);
      }
      // Zabezpečíme, že aktuálny mesiac je vždy rozbalený
      newExpanded.add(currentMonth);
    }
    
    setExpandedMonths(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-[400px] flex items-center justify-center">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <p>{t('tasks:error')}</p>
        </div>
      </div>
    );
  }

  // Výpočet celkovej sumy za všetky úlohy – používa API hodnoty
  const calculateTotalAmount = (tasks: Task[]) => {
    return tasks.reduce((sum, task) => {
      // Use API calculated totals if available
      if (task.subtotal_cents !== undefined && task.total_cents !== undefined) {
        // If user is not VAT payer, use subtotal (without VAT), otherwise use total (with VAT)
        return sum + (user?.isVatPayer ? (task.total_cents / 100) : (task.subtotal_cents / 100));
      }
      // Fallback to old calculation for backward compatibility
      const client = Array.isArray(clients) ? clients.find(c => c.id === task.clientId) : undefined;
      return sum + computeTaskTotal(task, client, user);
    }, 0);
  };

  const totalTasksAmount = calculateTotalAmount(filteredTasks || []);
  const groupedTasks = groupTasksByMonth(filteredTasks || [], clients, user) as Array<[string, Task[], number]>;

  return (
    <div className="page-container min-w-0">
      <div className="min-h-screen space-y-4">
        {/* Compact header - title + actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3">
          <div>
            <h1 className="page-title text-xl sm:text-2xl">{t('nav:tasks')}</h1>
            <p className="text-muted-foreground text-sm">{t('tasks:overviewDescription')}</p>
          </div>
          <div className="flex flex-col xs:flex-row gap-2">
            <Button
              variant="default"
              className="w-full sm:w-auto"
              onClick={() => setIsQuickCreateOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Rýchla úloha
            </Button>
            <Dialog open={isCreateDialogOpen} onOpenChange={handleCreateDialogOpenChange}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('tasks:createNew')}
                </Button>
              </DialogTrigger>
              <DialogContent
                className="max-w-4xl max-h-[90vh] overflow-auto bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30"
                onEscapeKeyDown={(e) => e.preventDefault()}
              >
                <DialogHeader>
                  <DialogTitle>{t('tasks:createNew')}</DialogTitle>
                  <DialogDescription>
                    {t('tasks:createDescription', 'Vytvorte novú úlohu s názvom, klientom a ďalšími detailami.')}
                  </DialogDescription>
                </DialogHeader>
                <EnhancedTaskForm
                  onNavigateToCreateClient={() => {
                    setIsCreateDialogOpen(false);
                    setLocation(getLocalizedUrl('/clients'));
                  }}
                  onSubmit={async (data, items) => {
                    // Vytvorenie úlohy s položkami v jednom volaní (form môže poslať client_id alebo clientId)
                    const rawClientId = data.clientId ?? data.client_id;
                    const toIso = (v: unknown) => v instanceof Date ? v.toISOString() : v;
                    const taskData = {
                      title: data.title,
                      description: data.description || "",
                      clientId: rawClientId != null ? parseInt(String(rawClientId)) : null,
                      hoursWorked: data.hours_worked ?? data.hoursWorked ? parseFloat(String(data.hours_worked ?? data.hoursWorked)) : null,
                      status: data.status,
                      taskType: data.task_type ?? data.type,
                      dueDate: toIso(data.due_date ?? data.dueDate) ?? null,
                      acceptDeadline: toIso(data.accept_deadline ?? data.acceptDeadline) ?? null,
                      isStarred: data.isStarred,
                      manualAmount: data.manualAmount ? parseFloat(String(data.manualAmount)) : null,
                      // Formulár odosiela assigned_to_id, nie assignedToId
                      assignedToId: data.assigned_to_id ? parseInt(data.assigned_to_id.toString()) : null,
                      assigned_to_id: data.assigned_to_id ? parseInt(data.assigned_to_id.toString()) : null,
                      items: items || [],
                    };
                    
                    console.log("[FRONTEND] Creating task with items:", items);
                    console.log("[FRONTEND] taskData:", taskData);
                    
                    createMutation.mutate(taskData, {
                      onSuccess: async (newTask) => {
                        // Položky sa ukladajú len v rámci POST /api/tasks – nie duplicitne v onSuccess
                        setIsCreateDialogOpen(false);
                      }
                    });
                  }}
                  clients={clients || []}
                  teamMembers={(teamMembers || []) as any}
                  isSubmitting={createMutation.isPending}
                  defaultValues={
                    selectedClientId && selectedClientId !== "all" 
                      ? { 
                          clientId: selectedClientId,
                          title: "",
                          hoursWorked: "0",
                          status: "pending" as const,
                          isStarred: false,
                          type: "standard" as const
                        }
                      : { 
                          clientId: selectedClientId && selectedClientId !== "all" ? selectedClientId : "",
                          title: "",
                          hoursWorked: "0",
                          status: "pending" as const,
                          isStarred: false,
                          type: "standard" as const
                        }
                  }
                />
              </DialogContent>
            </Dialog>
            <QuickTaskModal
              open={isQuickCreateOpen}
              // Close iba cez X (global click handler v tasks.tsx rieši zmenu state po kliknutí na X)
              onOpenChange={(open) => {
                if (open) setIsQuickCreateOpen(true);
              }}
              clients={clients || []}
              defaultClientId={selectedClientId && selectedClientId !== "all" ? parseInt(selectedClientId) : null}
              onCreateSuccess={() => {
                queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
              }}
            />
            <Dialog open={personalViewOpen} onOpenChange={setPersonalViewOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <Users className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">{t('tasks:personalView')}</span>
                  <span className="sm:hidden">Osobný pohľad</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
                <DialogHeader>
                  <DialogTitle>{t('tasks:personalView')}</DialogTitle>
                  <DialogDescription>
                    {t('tasks:personalViewDescription')}
                  </DialogDescription>
                </DialogHeader>
                <PersonalTaskView 
                  teamMembers={teamMembers || []}
                  tasks={tasks || []}
                  clients={clients || []}
                />
              </DialogContent>
            </Dialog>
            <Button 
              variant="outline" 
              className="w-full sm:w-auto"
              onClick={() => {
                const screenWidth = window.screen.width;
                const screenHeight = window.screen.height;
                const width = Math.min(1400, screenWidth * 0.9);
                const height = Math.min(900, screenHeight * 0.9);
                const left = (screenWidth - width) / 2;
                const top = (screenHeight - height) / 2;
                
                const newWindow = window.open(
                  getLocalizedUrl('/personal-tasks'), 
                  '_blank', 
                  `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no`
                );
                
                if (newWindow) {
                  newWindow.focus();
                } else {
                  toast({
                    title: t('common:error'),
                    description: t('tasks:popupBlocked'),
                    variant: "default",
                  });
                }
              }}
            >
              <Users className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{t('tasks:openInNewWindow')}</span>
              <span className="sm:hidden">Nové okno</span>
            </Button>
            <Dialog open={isCreateInvoiceDialogOpen} onOpenChange={setIsCreateInvoiceDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <FileText className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">{t('tasks:createInvoice')}</span>
                  <span className="sm:hidden">Vytvoriť faktúru</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
                <DialogHeader>
                  <DialogTitle>{t('tasks:createInvoice')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {t('tasks:selectTasksForInvoice')}
                    </p>
                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-3">
                      <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                        <span className="text-sm font-medium">💡 {t('tasks:checkboxTip')}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm font-medium">
{t('tasks:currentlySelected')}: {selectedTaskIds.length} {t('tasks:tasks')}
                  </p>
                  {selectedTaskIds.length > 0 && (() => {
                    const totals = calculateInvoiceTotals(selectedTaskIds);
                    // Invoice calculation for selected tasks
                    return (
                      <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                        <p className="text-sm font-medium text-green-800 dark:text-green-200">
{t('tasks:totalValue')}: {formatCurrency(totals.totalAmount)}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-300 mt-1">
{t('tasks:selectedTasks')}: {selectedTaskIds.join(", ")}
                        </p>
                      </div>
                    );
                  })()}
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setIsCreateInvoiceDialogOpen(false)}
                    >
{t('common:cancel')}
                    </Button>
                    <Button 
                      onClick={handleCreateInvoiceClick}
                      disabled={selectedTaskIds.length === 0}
                    >
{t('common:continue')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {planLimitError && (
          <Alert className="mb-6 border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/50 text-orange-800 dark:text-orange-300">
            <AlertTitle className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
{t('tasks:planLimitReached')}
            </AlertTitle>
            <AlertDescription className="mt-2">
{t('tasks:planLimitMessage')}
              <div className="mt-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-orange-200 bg-orange-100/50 hover:bg-orange-100 dark:border-orange-800 dark:bg-orange-900/30 dark:hover:bg-orange-900/50 text-orange-800 dark:text-orange-300"
                  onClick={() => setPlanLimitError(false)}
                >
{t('common:close')}
                </Button>
                <Button 
                  variant="default" 
                  size="sm" 
                  className="ml-2"
                  onClick={() => window.location.href = "/subscribe"}
                >
{t('tasks:upgradePlan')}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Compact filter toolbar - 1 row on desktop */}
        <div className="flex flex-col gap-2">
          <div className="tk-toolbar flex flex-wrap items-center gap-2 py-2 px-3 rounded-lg border bg-card/50 min-h-[44px]">
            <div className="relative flex-1 min-w-[140px] sm:min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('filters:search', { ns: 'filters' }) || 'Hľadať...'}
                className="pl-8 h-9 text-sm border"
                value={searchTerm || ''}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={selectedClientId || 'all'} onValueChange={(v) => setSelectedClientId(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-[140px] sm:w-[160px] text-sm border">
                <SelectValue placeholder={t('filters:allClients', { ns: 'filters' }) || 'Všetci klienti'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters:allClients', { ns: 'filters' }) || 'Všetci klienti'}</SelectItem>
                {(clients || []).map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.businessName || c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-[120px] sm:w-[140px] text-sm border">
                <SelectValue placeholder={t('filters:allStatuses', { ns: 'filters' }) || 'Všetky stavy'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters:allStatuses', { ns: 'filters' }) || 'Všetky stavy'}</SelectItem>
                <SelectItem value="pending">{t('tasks:statusPending')}</SelectItem>
                <SelectItem value="in_progress">{t('tasks:statusInProgress')}</SelectItem>
                <SelectItem value="completed">{t('tasks:statusCompleted')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedTeamMemberId || 'all'} onValueChange={(v) => setSelectedTeamMemberId(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-[120px] sm:w-[140px] text-sm border">
                <SelectValue placeholder={t('filters:allEmployees', { ns: 'filters' }) || 'Všetci zamestnanci'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters:allEmployees', { ns: 'filters' }) || 'Všetci zamestnanci'}</SelectItem>
                <SelectItem value="unassigned">{(user as any)?.companyName || (user as any)?.businessName || t('filters:unassigned', { ns: 'filters' }) || 'Nepriradené'}</SelectItem>
                {(teamMembers || []).map((m) => (
                  <SelectItem key={m.id} value={String(m.id)}>{m.memberName || m.invitedEmail}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" className="h-9 px-2" onClick={() => {
              setSearchTerm('');
              setSelectedClientId(undefined);
              setStatusFilter(undefined);
              setSelectedTeamMemberId(undefined);
              setStarredFilter(undefined);
              setHideInvoicedTasks(false);
            }}>
              <Filter className="h-4 w-4 mr-1" />
              {t('filters:clear', { ns: 'filters' }) || 'Reset'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-2 text-muted-foreground"
              onClick={() => setAdvancedFiltersOpen(!advancedFiltersOpen)}
            >
              {advancedFiltersOpen ? (
                <><ChevronDown className="h-4 w-4 mr-1" />{t('common:collapse', { ns: 'common' }) || 'Skryť'} pokročilé</>
              ) : (
                <><ChevronRight className="h-4 w-4 mr-1" />{t('common:expand', { ns: 'common' }) || 'Zobraziť'} pokročilé</>
              )}
            </Button>
          </div>
          {/* Advanced filters - hidden by default */}
          {advancedFiltersOpen && (
            <div className="flex flex-wrap items-center gap-3 py-2 px-3 rounded-lg border border-dashed bg-muted/30">
              <Select value={starredFilter === undefined ? 'all' : starredFilter ? 'starred' : 'not_starred'} onValueChange={(v) => setStarredFilter(v === 'all' ? undefined : v === 'starred')}>
                <SelectTrigger className="h-9 w-[140px] text-sm">
                  <SelectValue placeholder={t('filters:marking', { ns: 'filters' }) || 'Označenie'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('filters:allTasks', { ns: 'filters' }) || 'Všetky úlohy'}</SelectItem>
                  <SelectItem value="starred">{t('filters:starred', { ns: 'filters' }) || 'Označené'}</SelectItem>
                  <SelectItem value="not_starred">{t('filters:notStarred', { ns: 'filters' }) || 'Neoznačené'}</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Checkbox id="hideInvoiced" checked={hideInvoicedTasks} onCheckedChange={(c) => setHideInvoicedTasks(c === true)} />
                <label htmlFor="hideInvoiced" className="text-sm cursor-pointer">{t('tasks:hideInvoicedTasks') || 'Skryť vyfaktúrované'}</label>
              </div>
            </div>
          )}
        </div>

        {/* Compact inline summary - thin bar ~40-48px */}
        <div className="flex flex-wrap items-center justify-between gap-2 py-2 px-3 rounded-md border bg-muted/20">
          <span className="text-sm text-foreground/80">{t('tasks:overview') || 'Prehľad'}</span>
          <div className="flex items-center gap-4 text-sm">
            <span><span className="font-semibold">{totalTasksAmount.toFixed(2)} €</span></span>
            <span className="text-foreground/80">{filteredTasks?.length || 0} {t('tasks:tasksCount') || 'úloh'}</span>
            {(filteredTasks?.filter(t => !t.isInvoiced).length ?? 0) > 0 && (
              <span className="text-foreground/80">{filteredTasks?.filter(t => !t.isInvoiced).length} nevyfaktúrovaných</span>
            )}
          </div>
        </div>

        {groupedTasks.length === 0 && (
          <div className="w-full min-h-[400px] flex items-center justify-center">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <ClipboardList className="h-20 w-20 text-muted-foreground" strokeWidth={1.5} />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">{t('tasks:noTasks')}</h3>
                <p className="text-muted-foreground">{t('tasks:noTasksDescription')}</p>
              </div>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t('tasks:createNew')}
              </Button>
            </div>
          </div>
        )}

        {Array.isArray(groupedTasks) ? groupedTasks.map(([month, monthTasks, monthAmount]) => (
          <div key={month.toString()} className="mb-4">
            <div className="mb-2 border rounded-lg overflow-hidden">
              <Button
                variant="ghost"
                className="tk-month-header w-full flex justify-between items-center py-2 px-3 h-auto min-h-[40px] bg-gradient-to-r from-primary/5 to-primary/10 touch-manipulation"
                onClick={() => toggleMonth(month.toString())}
              >
                <div className="flex items-center gap-3">
                  {expandedMonths.has(month.toString()) ? (
                    <ChevronDown className="h-5 w-5 text-primary shrink-0" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-primary shrink-0" />
                  )}
                  <div className="flex flex-col items-start text-left">
                    <span className="font-semibold text-base sm:text-lg text-primary">{formatMonth(month.toString())}</span>
                    <span className="text-xs sm:text-sm text-muted-foreground">Celková suma: {formatMoney(monthAmount * 100)}</span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-2">
                  <Badge variant="secondary" className="rounded-full py-0.5 px-2 sm:px-3 text-xs sm:text-sm">
                    {monthTasks.length} {monthTasks.length === 1 ? 'úloha' : monthTasks.length < 5 ? 'úlohy' : 'úloh'}
                  </Badge>
                  <span className="text-xs text-muted-foreground sm:hidden">
                    {expandedMonths.has(month.toString()) ? 'Skryť' : 'Zobraziť'}
                  </span>
                </div>
              </Button>
            </div>

            {expandedMonths.has(month.toString()) && (
              <div className="rounded-md border">
                {/* Desktop Table - bez horizontálneho scrollu */}
                <div className="hidden lg:block w-full min-w-0 overflow-hidden">
                  <Table className="tk-row w-full">
                  <TableHeader className="bg-primary/5 dark:bg-primary/10 sticky top-0 z-10 backdrop-blur-md bg-background/95">
                    <TableRow>
                      <TableHead className="w-[44px] shrink-0 font-bold text-sm uppercase text-foreground">{t('select', { ns: 'tasks' }) || 'Vybrať'}</TableHead>
                      <TableHead className="w-[90px] shrink-0 font-bold text-sm uppercase text-foreground">Č.</TableHead>
                      <TableHead className="min-w-0 font-bold text-sm uppercase text-foreground">{t('taskName', { ns: 'tasks' }) || 'Názov úlohy'}</TableHead>
                      <TableHead className="w-[100px] shrink-0 font-bold text-sm uppercase text-foreground">{t('status', { ns: 'tasks' }) || 'Stav'}</TableHead>
                      <TableHead className="w-[100px] shrink-0 font-bold text-sm uppercase text-foreground">{t('dueDate', { ns: 'tasks' }) || 'Termín'}</TableHead>
                      <TableHead className="w-[100px] shrink-0 font-bold text-sm uppercase text-foreground">{t('tasks:form.acceptDeadline') || 'Odovzdanie'}</TableHead>
                      <TableHead className="w-[90px] shrink-0 font-bold text-sm uppercase text-foreground text-right">{t('amount', { ns: 'tasks' }) || 'Suma'}</TableHead>
                      <TableHead className="w-[120px] shrink-0 text-right font-bold text-sm uppercase text-foreground">{typeof t('actions', { ns: 'tasks' }) === 'string' ? t('actions', { ns: 'tasks' }) : 'Akcie'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.isArray(monthTasks) && monthTasks.length > 0 ? monthTasks
                      .filter(task => task && typeof task === 'object' && task !== null)
                      .map((task, index) => {
                      const client = Array.isArray(clients) ? clients.find((c) => c.id === task.clientId) : undefined;
                      
                      // Use API calculated totals if available
                      // If user is not VAT payer, use subtotal (without VAT), otherwise use total (with VAT)
                      let total = 0;
                      if (task.subtotal_cents !== undefined && task.total_cents !== undefined) {
                        total = (user?.isVatPayer === true) ? (task.total_cents / 100) : (task.subtotal_cents / 100);
                      } else {
                        total = computeTaskTotal(task, client, user);
                      }
                      
                      
                      // Add classes for invoiced tasks - make them lighter and add a visual indicator
                      const isInvoiced = task.isInvoiced;
                      const rowClasses = `${isInvoiced ? "opacity-60" : ""} ${index % 2 === 0 ? "bg-muted/20" : ""}`;

                      return (
                        <TableRow
                          key={task.id}
                          className={`${rowClasses} cursor-pointer hover:bg-muted/50`}
                          onClick={() => {
                            setSelectedTaskId(task.id);
                            setDrawerOpen(true);
                          }}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()} className="w-[44px]">
                            <Checkbox
                              checked={selectedTaskIds.includes(task.id)}
                              disabled={isInvoiced && !!task.invoiceId}
                              onCheckedChange={() => toggleTaskSelection(task.id)}
                            />
                          </TableCell>
                          <TableCell className="w-[90px] shrink-0 font-mono text-sm">
                            {task.displayNumber ?? task.id.toString().padStart(4, '0')}
                          </TableCell>
                          <TableCell className="min-w-0 max-w-[280px]">
                            <div className="flex items-center gap-2 min-w-0">
                              {task.isStarred && (
                                <Star className="h-4 w-4 shrink-0 text-yellow-400 fill-yellow-400" />
                              )}
                              <TaskTypeBadge type={task.type || "standard"} size="sm" />
                              <div className="min-w-0 flex-1">
                                <div className="whitespace-nowrap overflow-hidden text-ellipsis font-medium flex items-center gap-1">
                                  <span className={isInvoiced ? "line-through" : ""}>{task.title}</span>
                                  <ChevronRight className="h-4 w-4 shrink-0 text-foreground/60" />
                                </div>
                                <div className="text-xs truncate">
                                  {client?.businessName || client?.name ? (
                                    <span className="text-foreground/75">{client.businessName || client.name}</span>
                                  ) : (
                                    <span className="text-destructive text-[11px]">bez klienta</span>
                                  )}
                                </div>
                              </div>
                              {isInvoiced && (
                                <Badge variant="outline" className="shrink-0 text-xs">
                                  {t('tasks:invoiced')}
                                </Badge>
                              )}
                              <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                {(hasPermission('tasks', 'edit') || hasPermission('tasks', 'view')) && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingTaskId(task.id)} title={t('common:edit') || 'Upraviť'}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="shrink-0">
                            <Badge
                              variant={
                                task.status === "completed"
                                  ? "default"
                                  : task.status === "in_progress"
                                    ? "secondary"
                                    : "outline"
                              }
                            >
                              {t(`tasks:statuses.${task.status}`)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-foreground/80 shrink-0">
                            {task.dueDate
                              ? (() => {
                                  const date = new Date(task.dueDate);
                                  return isNaN(date.getTime()) ? "—" : date.toLocaleDateString("sk-SK");
                                })()
                              : "—"}
                          </TableCell>
                          <TableCell className="text-foreground/80 shrink-0">
                            {task.acceptDeadline ?? task.accept_deadline
                              ? (() => {
                                  const date = new Date(task.acceptDeadline ?? task.accept_deadline);
                                  return isNaN(date.getTime()) ? "—" : date.toLocaleDateString("sk-SK");
                                })()
                              : "—"}
                          </TableCell>
                          <TableCell className="text-right font-medium shrink-0">
                            {total > 0 ? formatCurrency(total) : "—"}
                          </TableCell>
                          <TableCell className="text-right shrink-0" onClick={(e) => e.stopPropagation()}>
                            <div className="inline-flex items-center justify-end gap-0.5">
                            {(hasPermission('tasks', 'edit') || hasPermission('tasks', 'view')) && (
                              <Button 
                                size="icon" 
                                variant="ghost"
                                className={`h-8 w-8 inline-flex items-center justify-center ${task.isStarred ? "text-yellow-400" : ""}`}
                                onClick={() => toggleStarMutation.mutate({ id: task.id, isStarred: !task.isStarred })}
                                disabled={toggleStarMutation.isPending}
                                title={t('tasks:starTask') || 'Označiť'}
                              >
                                <Star className={`h-4 w-4 ${task.isStarred ? "fill-yellow-400" : ""}`} />
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-8 w-8 inline-flex items-center justify-center" title={t('common:more') || 'Ďalšie'}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => duplicateMutation.mutate(task.id)}
                                  disabled={duplicateMutation.isPending}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  {t('tasks:duplicateTask') || 'Duplikovať úlohu'}
                                </DropdownMenuItem>
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                      <Share2 className="h-4 w-4 mr-2" />
                                      {t('tasks:shareTask')}
                                    </DropdownMenuItem>
                                  </DialogTrigger>
                              <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                  <DialogTitle>{t('tasks:shareTask')}</DialogTitle>
                                  <DialogDescription>
{t('tasks:shareTaskDescription')}
                                  </DialogDescription>
                                </DialogHeader>
                                
                                <div className="space-y-6 py-4">
                                  <div className="space-y-2">
                                    <div className="flex items-center">
                                      <Badge variant="secondary" className="mr-2">
{t('tasks:taskLabel')} #{task.displayNumber ?? task.id}
                                      </Badge>
                                      <span className="font-medium text-sm">{task.title}</span>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-3">
                                    <h4 className="text-sm font-medium">{t('tasks:sharedLink')}</h4>
                                    <div className="flex items-center space-x-2">
                                      <div className="grid flex-1 gap-2">
                                        <Input 
                                          readOnly
                                          value={`${window.location.origin}/shared-task/${task.id}?token=shared-${task.clientId || 'unknown'}-${Date.now()}`}
                                          className="h-9"
                                        />
                                      </div>
                                      <Button
                                        size="sm"
                                        className="px-3"
                                        onClick={() => {
                                          const token = `shared-${task.clientId || 'unknown'}-${Date.now()}`;
                                          navigator.clipboard.writeText(`${window.location.origin}/shared-task/${task.id}?token=${token}`);
                                          toast({
                                            title: t('tasks:linkCopied'),
                                            description: t('tasks:taskLinkCopied'),
                                          });
                                        }}
                                      >
                                        <Link className="h-4 w-4 mr-2" />
                                        {t('common:copy')}
                                      </Button>
                                    </div>
                                    
                                    <div className="text-xs text-muted-foreground mt-2">
                                      Tento odkaz je verejne prístupný a umožňuje komukoľvek vidieť detaily tejto úlohy bez nutnosti prihlásenia.
                                    </div>
                                  </div>
                                  
                                  {task.clientId && (
                                    <div className="space-y-3 border-t pt-4">
                                      <h4 className="text-sm font-medium">Zdieľať všetky úlohy klienta</h4>
                                      <div className="flex items-center space-x-2">
                                        <div className="grid flex-1 gap-2">
                                          <Input 
                                            readOnly
                                            value={`${window.location.origin}/shared-client-tasks/${task.clientId}?token=shared-${task.clientId}-${Date.now()}`}
                                            className="h-9"
                                          />
                                        </div>
                                        <Button
                                          size="sm"
                                          className="px-3"
                                          onClick={() => {
                                            const token = `shared-${task.clientId}-${Date.now()}`;
                                            navigator.clipboard.writeText(`${window.location.origin}/shared-client-tasks/${task.clientId}?token=${token}`);
                                            toast({
                                              title: "Odkaz skopírovaný",
                                              description: "Odkaz na všetky úlohy klienta bol skopírovaný do schránky",
                                            });
                                          }}
                                        >
                                          <Users className="h-4 w-4 mr-2" />
                                          {t('common:copy')}
                                        </Button>
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        Tento odkaz umožňuje klientovi vidieť všetky jeho úlohy na jednej stránke bez nutnosti prihlásenia.
                                      </div>
                                    </div>
                                  )}
                                  
                                  <div className="space-y-3 border-t pt-4">
                                    <h4 className="text-sm font-medium">Poslať e-mailom</h4>
                                    {client && (
                                      <div className="space-y-3">
                                        <Button
                                          variant="outline"
                                          className="w-full justify-start"
                                          onClick={() => {
                                            const subject = encodeURIComponent(`Zdieľaná úloha: ${task.title}`);
                                            const body = encodeURIComponent(
                                              `Dobrý deň,\n\n` +
                                              `Zdieľam s Vami odkaz na úlohu "${task.title}".\n\n` +
                                              `Pre zobrazenie detailov úlohy kliknite na nasledujúci odkaz:\n` +
                                              `${window.location.origin}/shared-task/${task.id}?token=shared-${task.clientId || 'unknown'}-${Date.now()}\n\n` +
                                              `S pozdravom`
                                            );
                                            window.open(`mailto:${client.email}?subject=${subject}&body=${body}`);
                                          }}
                                        >
                                          <Mail className="h-4 w-4 mr-2" />
                                          <span className="truncate">{client.email}</span>
                                        </Button>
                                        
                                        {client.invoiceEmail && client.invoiceEmail !== client.email && (
                                          <Button
                                            variant="outline"
                                            className="w-full justify-start"
                                            onClick={() => {
                                              const subject = encodeURIComponent(`Zdieľaná úloha: ${task.title}`);
                                              const body = encodeURIComponent(
                                                `Dobrý deň,\n\n` +
                                                `Zdieľam s Vami odkaz na úlohu "${task.title}".\n\n` +
                                                `Pre zobrazenie detailov úlohy kliknite na nasledujúci odkaz:\n` +
                                                `${window.location.origin}/shared-task/${task.id}?token=sdj83jSdjisdj29sdSdjw\n\n` +
                                                `S pozdravom`
                                              );
                                              window.open(`mailto:${client.invoiceEmail}?subject=${subject}&body=${body}`);
                                            }}
                                          >
                                            <Mail className="h-4 w-4 mr-2" />
                                            <span className="truncate">{client.invoiceEmail} (fakturačný)</span>
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                <DialogFooter>
                                  <DialogTrigger asChild>
                                    <Button variant="outline">Zavrieť</Button>
                                  </DialogTrigger>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    }) : null}
                    {/* Súhrnný riadok na konci tabuľky */}
                    <TableRow className="bg-primary/5 dark:bg-primary/10 font-bold">
                      <TableCell colSpan={6} className="text-right">Súhrn za {formatMonth(month.toString())}:</TableCell>
                      <TableCell className="text-right">{formatCurrency(monthAmount)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              
              {/* Mobile Card View */}
              <div className="lg:hidden space-y-3 p-3">
                {Array.isArray(monthTasks) ? monthTasks.map((task, index) => {
                  const client = Array.isArray(clients) ? clients.find((c) => c.id === task.clientId) : undefined;
                  
                  // Use API calculated totals if available
                  // If user is not VAT payer, use subtotal (without VAT), otherwise use total (with VAT)
                  let total = 0;
                  if (task.subtotal_cents !== undefined && task.total_cents !== undefined) {
                    total = (user?.isVatPayer === true) ? (task.total_cents / 100) : (task.subtotal_cents / 100);
                  } else {
                    total = computeTaskTotal(task, client, user);
                  }
                  
                  const isInvoiced = task.isInvoiced;
                  const assignedTo = teamMembers?.find(m => m.id === task.assignedToId);
                  
                  return (
                    <div
                      key={task.id}
                      className={`bg-card border-2 rounded-xl p-4 space-y-3 transition-all cursor-pointer ${isInvoiced ? "opacity-60 border-gray-200" : "border-gray-300 hover:border-primary/30"}`}
                      onClick={() => {
                        setSelectedTaskId(task.id);
                        setDrawerOpen(true);
                      }}
                    >
                      {/* Mobile Task Header with checkbox */}
                      <div className="flex items-start gap-3">
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                          checked={selectedTaskIds.includes(task.id)}
                          disabled={isInvoiced && !!task.invoiceId}
                          onCheckedChange={() => toggleTaskSelection(task.id)}
                          className="mt-1"
                        />
                        </div>
                        <div className="flex-1 space-y-2">
                          {/* Title and badges */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {task.isStarred && <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />}
                                <span className="text-xs text-muted-foreground font-mono">#{task.displayNumber ?? task.id.toString().padStart(4, '0')}</span>
                              </div>
                              <h4 className={`font-semibold text-base ${isInvoiced ? "line-through text-muted-foreground" : ""}`}>
                                {task.title}
                              </h4>
                            </div>
                            <TaskTypeBadge type={task.type || "standard"} size="sm" />
                          </div>
                          
                          {/* Client and status info */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-foreground/70">Klient:</span>
                              <span className={`font-medium truncate max-w-[60%] ${client?.businessName || client?.name ? "text-foreground" : "text-destructive text-xs"}`}>
                                {client?.businessName || client?.name || "bez klienta"}
                              </span>
                            </div>
                            {assignedTo && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-foreground/70">Priradené:</span>
                                <span className="truncate max-w-[60%]">{assignedTo.invitedEmail}</span>
                              </div>
                            )}
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-foreground/70">Stav:</span>
                              <Badge 
                                variant={
                                  task.status === 'completed' ? 'default' : 
                                  task.status === 'in_progress' ? 'secondary' : 
                                  'outline'
                                }
                                className="text-xs"
                              >
                                {task.status === 'pending' ? 'Čaká' : 
                                 task.status === 'in_progress' ? 'V procese' : 
                                 task.status === 'completed' ? 'Dokončená' : 
                                 task.status}
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Dates and amount */}
                          <div className="pt-2 border-t space-y-1">
                            {task.dueDate && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-foreground/70">{t('tasks:dueDate')}:</span>
                                <span className="font-medium">{new Date(task.dueDate).toLocaleDateString('sk-SK')}</span>
                              </div>
                            )}
                            {(task.acceptDeadline ?? task.accept_deadline) && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-foreground/70">{t('tasks:form.acceptDeadline') || 'Odovzdanie'}:</span>
                                <span className="font-medium">{new Date(task.acceptDeadline ?? task.accept_deadline).toLocaleDateString('sk-SK')}</span>
                              </div>
                            )}
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-foreground/70">{t('tasks:hours')}:</span>
                              <span>{(() => {
                                const hours = parseFloat(task.hoursWorked || '0');
                                return isNaN(hours) ? '0.00' : hours.toFixed(2);
                              })()}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm font-semibold">
                              <span className="text-foreground/70">{t('tasks:amount')}:</span>
                              <span className="text-primary">{formatCurrency(total)}</span>
                            </div>
                          </div>
                          
                          {isInvoiced && (
                            <Badge variant="outline" className="w-full justify-center">
                              <CreditCard className="h-3 w-3 mr-1" />
                              {t('tasks:invoiced')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Mobile action buttons - responsive grid */}
                      <div className="grid grid-cols-3 gap-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
                        {(hasPermission('tasks', 'edit') || hasPermission('tasks', 'view')) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleStarMutation.mutate({ id: task.id, isStarred: !task.isStarred })}
                            className={`${task.isStarred ? "text-yellow-400" : ""} touch-manipulation`}
                            disabled={toggleStarMutation.isPending}
                          >
                            <Star className={`h-4 w-4 ${task.isStarred ? "fill-yellow-400" : ""}`} />
                          </Button>
                        )}
                        {(hasPermission('tasks', 'edit') || hasPermission('tasks', 'view')) && (
                          <Dialog
                            open={editingTaskId === task.id}
                            onOpenChange={(open) => {
                              if (open) setEditingTaskId(task.id);
                              // pri close-openChanges (napr. klik mimo) necháme dialog zavretý až cez X
                            }}
                          >
                            <DialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="touch-manipulation">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
                              <DialogHeader>
                                <DialogTitle>{t('editTask', { ns: 'tasks' }) || 'Upraviť úlohu'}</DialogTitle>
                              </DialogHeader>
                              <EnhancedTaskForm
                                taskId={task.id}
                                clients={clients || []}
                                teamMembers={(teamMembers || []) as any}
                                onNavigateToCreateClient={() => {
                                  setEditingTaskId(null);
                                  setLocation(getLocalizedUrl('/clients'));
                                }}
                                onSubmit={async (data, items) => {
                                  console.log('[FRONTEND] Task update (3rd instance) - items:', items);
                                  console.log('[FRONTEND] Task update (3rd instance) - data:', data);
                                  console.log('[FRONTEND] Task update (3rd instance) - task.id:', task.id);
                                  // data is already backendData in snake_case from enhanced-task-form
                                  const taskData = {
                                    ...data,
                                    // Ensure assigned_to_id is properly sent
                                    assigned_to_id: data.assigned_to_id || null
                                    // items sa už neposielajú - ukladajú sa individuálne
                                  };
                                  
                                  updateMutation.mutate({ id: task.id, data: taskData }, {
                                    onSuccess: async (response) => {
                                      // Úplne vymažeme cache a vynútime nové načítanie
                                      queryClient.removeQueries({ queryKey: ["/api/tasks"] });
                                      queryClient.removeQueries({ queryKey: ["/api/tasks/statistics"] });
                                      await queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
                                      await queryClient.refetchQueries({ queryKey: ["/api/tasks/statistics"] });
                                      
                                      // Načítame aktuálne položky z databázy
                                      console.log('[FRONTEND] Update mutation success, response:', response);
                                      
                                      // Načítame aktuálne položky z databázy
                                      try {
                                        const itemsResponse = await fetch(`/api/tasks/${task.id}/items`, {
                                          credentials: 'include'
                                        });
                                        if (itemsResponse.ok) {
                                          const itemsData = await itemsResponse.json();
                                          console.log('[FRONTEND] Loaded fresh items from database:', itemsData);
                                          // Položky sa načítajú automaticky v EnhancedTaskForm komponente
                                        }
                                      } catch (error) {
                                        console.error('[FRONTEND] Error loading fresh items:', error);
                                      }
                                      
                                      // Nezatvárame editáciu, aby sa položky načítali
                                      // setEditingTaskId(null);
                                    }
                                  });
                                }}
                                onSuccess={undefined}
                                defaultValues={{
                                  title: task.title,
                                  description: task.description || "",
                                  clientId: (task.clientId ?? task.client_id)?.toString() || "",
                                  hoursWorked: (task.hoursWorked ?? task.hours_worked)?.toString() || "0",
                                  status: task.status,
                                  type: task.type ?? task.task_type,
                                  dueDate: (task.dueDate ?? task.due_date) ? new Date(task.dueDate ?? task.due_date) : undefined,
                                  acceptDeadline: (task.acceptDeadline ?? task.accept_deadline) ? new Date(task.acceptDeadline ?? task.accept_deadline) : undefined,
                                  isStarred: task.isStarred,
                                  manualAmount: task.manualAmount?.toString() || "",
                                  assignedToId: task.assignedToId?.toString() || ""
                                }}
                                isSubmitting={updateMutation.isPending}
                              />
                            </DialogContent>
                          </Dialog>
                        )}
                      </div>
                    </div>
                  );
                }) : null}
              </div>
            </div>
            )}
          </div>
        )) : null}

        {/* Load more (older tasks) */}
        <div className="py-6 flex items-center justify-center" ref={loadMoreRef}>
          {hasNextPage ? (
            <Button
              variant="outline"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Načítavam…
                </>
              ) : (
                "Načítať staršie úlohy"
              )}
            </Button>
          ) : (
            <div className="text-sm text-muted-foreground">
              Staršie úlohy už nie sú.
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Button for Quick Invoice Creation */}
      {selectedTaskIds.length > 0 && (() => {
        console.log('🎯 [FAB] Rendering floating invoice button, selectedTaskIds:', selectedTaskIds.length);
        return (
          <div 
            className="fixed right-4 z-[100] bottom-28 sm:bottom-6"
            style={{ 
              position: 'fixed',
              zIndex: 100,
              pointerEvents: 'auto',
            }}
          >
            <div className="bg-card border-2 border-primary/30 rounded-lg shadow-2xl p-3 sm:p-4 flex flex-col sm:flex-row items-center gap-2 sm:gap-3 min-w-[260px] sm:min-w-[400px] max-w-[calc(100vw-2rem)] backdrop-blur-sm">
            <div className="flex-1 text-center sm:text-left min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {selectedTaskIds.length} {selectedTaskIds.length === 1 ? t('tasks:task') || 'úloha' : t('tasks:tasks') || 'úloh'} {t('tasks:selected') || 'vybraných'}
              </p>
              {(() => {
                const totals = calculateInvoiceTotals(selectedTaskIds);
                return (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('tasks:totalValue')}: {formatCurrency(totals.totalAmount)}
                  </p>
                );
              })()}
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedTaskIds([])}
                className="shrink-0 flex-1 sm:flex-none"
              >
                {t('common:cancel')}
              </Button>
              <Button
                onClick={handleCreateInvoiceClick}
                size="sm"
                className="shrink-0 flex-1 sm:flex-none bg-primary hover:bg-primary/90"
              >
                <FileText className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('tasks:createInvoice')}</span>
                <span className="sm:hidden">Faktúra</span>
              </Button>
            </div>
          </div>
        </div>
        );
      })()}

      <TaskDrawer
        taskId={selectedTaskId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        clients={clients || []}
        teamMembers={(teamMembers || []) as any}
        user={user ? { isVatPayer: (user as any).isVatPayer } : undefined}
        onEdit={(id) => {
          setEditingTaskId(id);
          setDrawerOpen(false);
        }}
        onMarkDone={(id) => {
          updateMutation.mutate({ id, data: { status: "completed" } });
          setDrawerOpen(false);
        }}
        onCreateInvoice={(id) => {
          setSelectedTaskIds([id]);
          setDrawerOpen(false);
          setIsCreateInvoiceDialogOpen(true);
        }}
        onShare={(id) => {
          const tsk = tasks?.find((t) => t.id === id);
          if (tsk) {
            const token = `shared-${tsk.clientId || "unknown"}-${Date.now()}`;
            navigator.clipboard.writeText(`${window.location.origin}/shared-task/${id}?token=${token}`);
            toast({ title: t("tasks:linkCopied"), description: t("tasks:taskLinkCopied") });
          }
        }}
        onDuplicate={(id) => {
          duplicateMutation.mutate(id);
          setDrawerOpen(false);
        }}
        onDelete={(id) => {
          deleteMutation.mutate(id);
          setDrawerOpen(false);
        }}
      />

      {editingTaskId && (
        <Dialog
          open={!!editingTaskId}
          onOpenChange={handleEditDialogOpenChange}
        >
          <DialogContent
            className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30"
            // Close the modal only via the X button; outside clicks shouldn't close it.
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle>{t("editTask", { ns: "tasks" }) || "Upraviť úlohu"}</DialogTitle>
            </DialogHeader>
            {(() => {
              const task = tasks?.find((t) => t.id === editingTaskId);
              if (!task) return null;
              return (
                <ErrorBoundary
                  fallbackRender={({ error }) => (
                    <div className="p-4 text-red-600 text-sm whitespace-pre-wrap">
                      <div className="font-semibold">Form render error</div>
                      {String(error?.message || error)}
                    </div>
                  )}
                >
                  <EnhancedTaskForm
                    taskId={task.id}
                    onNavigateToCreateClient={() => {
                      setEditingTaskId(null);
                      setLocation(getLocalizedUrl('/clients'));
                    }}
onSubmit={async (data, items) => {
                        // data je backendData z EnhancedTaskForm (client_id, hours_worked, type, task_type, ...)
                        const taskData = {
                          ...data,
                          assigned_to_id: data.assigned_to_id ?? null,
                        };
                        updateMutation.mutate({ id: task.id, data: taskData }, {
                          onSuccess: () => {
                            setEditingTaskId(null);
                            queryClient.removeQueries({ queryKey: ["/api/tasks"] });
                            queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
                          },
                        });
                      }}
                    defaultValues={{
                      title: task.title,
                      description: task.description || "",
                      clientId: task.clientId?.toString() || "",
                      hoursWorked: task.hoursWorked?.toString() || "0",
                      status: task.status,
                      type: task.type ?? task.task_type,
                      dueDate: (task.dueDate ?? task.due_date) ? new Date(task.dueDate ?? task.due_date) : undefined,
                      acceptDeadline: (task.acceptDeadline ?? task.accept_deadline) ? new Date(task.acceptDeadline ?? task.accept_deadline) : undefined,
                      isStarred: task.isStarred,
                      manualAmount: task.manualAmount?.toString() || "",
                      assignedToId: task.assignedToId?.toString() || "",
                      subtotal_cents: task.subtotal_cents,
                      tax_cents: task.tax_cents,
                      total_cents: task.total_cents,
                    }}
                    clients={clients || []}
                    teamMembers={(teamMembers || []) as any}
                    isSubmitting={updateMutation.isPending}
                  />
                </ErrorBoundary>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
/* Cache bust 1760792152 */

/* Cache bust 1760792152 */
